export const RESOURCE_NUMERIC_KEYS = [
  "Unit Price",
  "unit_price",
  "min_value",
  "max_value",
  "vram_size_gb",
  "gpu_count",
  "vcpus",
  "memory_gb",
  "memory_allocated_gb",
];

/** Fields rendered by the form chrome, not as generic spec inputs. */
const SPECIAL_FIELD_KEYS = new Set([
  "Resource Name",
  "Resource Description",
  "Category",
  "Resource Category",
  "Unit",
  "Unit Price",
  "unit_price",
  "min_value",
  "max_value",
  "Avalability",
]);

export function getCategoryFieldKey(schema) {
  if (schema?.properties?.Category) return "Category";
  if (schema?.properties?.["Resource Category"]) return "Resource Category";
  return "Category";
}

function normalizeFormDataArg(schema, formDataOrCategory, unit) {
  if (formDataOrCategory && typeof formDataOrCategory === "object") {
    return formDataOrCategory;
  }

  const categoryKey = getCategoryFieldKey(schema);
  return {
    [categoryKey]: formDataOrCategory,
    Unit: unit || "",
  };
}

function findOneOfBranch(oneOf, depKey, value) {
  if (!Array.isArray(oneOf) || !oneOf.length) return null;

  if (value !== undefined && value !== null && value !== "") {
    const match = oneOf.find(
      (branch) =>
        String(branch?.properties?.[depKey]?.const ?? "").toLowerCase() ===
        String(value).toLowerCase()
    );
    if (match) return match;
  }

  return oneOf[0];
}

function isConstOnlyField(fieldSchema) {
  return (
    fieldSchema &&
    fieldSchema.const !== undefined &&
    !Array.isArray(fieldSchema.enum)
  );
}

/**
 * Walk properties + nested dependencies/oneOf, merging fields for the
 * currently selected (or default) branch values in formData.
 */
function collectResolvedFields(schemaNode, formData, acc = {}) {
  if (!schemaNode || typeof schemaNode !== "object") return acc;

  Object.entries(schemaNode.properties || {}).forEach(([key, fieldSchema]) => {
    if (isConstOnlyField(fieldSchema)) return;
    acc[key] = fieldSchema;
  });

  Object.entries(schemaNode.dependencies || {}).forEach(([depKey, dependency]) => {
    if (!Array.isArray(dependency?.oneOf)) return;

    const branch = findOneOfBranch(dependency.oneOf, depKey, formData?.[depKey]);
    if (!branch) return;

    // Ensure the discriminator itself is present with a usable schema.
    const branchDiscriminator = branch.properties?.[depKey];
    if (branchDiscriminator && !isConstOnlyField(branchDiscriminator)) {
      acc[depKey] = branchDiscriminator;
    } else if (schemaNode.properties?.[depKey] && !isConstOnlyField(schemaNode.properties[depKey])) {
      acc[depKey] = schemaNode.properties[depKey];
    }

    // Prefer the selected const so nested deps resolve consistently.
    const constValue = branch.properties?.[depKey]?.const;
    const nextFormData =
      constValue !== undefined &&
      (formData?.[depKey] === undefined ||
        formData?.[depKey] === null ||
        formData?.[depKey] === "")
        ? { ...formData, [depKey]: constValue }
        : formData;

    collectResolvedFields(branch, nextFormData, acc);
  });

  return acc;
}

export function getResolvedFields(schema, formData = {}) {
  if (!schema) return {};
  return collectResolvedFields(
    {
      properties: schema.properties || {},
      dependencies: schema.dependencies || {},
    },
    formData,
    {}
  );
}

function getDependencyBranch(schema, depKey, constValue) {
  const oneOf = schema?.dependencies?.[depKey]?.oneOf || [];
  return findOneOfBranch(oneOf, depKey, constValue);
}

export function getDependentProperties(schema, depKey, constValue) {
  const branch = getDependencyBranch(schema, depKey, constValue);
  if (!branch?.properties) return {};
  const { [depKey]: _omit, ...rest } = branch.properties;
  return rest;
}

export function getUnitOptions(schema, formDataOrCategory, unit) {
  const formData = normalizeFormDataArg(schema, formDataOrCategory, unit);
  const resolved = getResolvedFields(schema, formData);
  const unitField = resolved.Unit || {};
  const enums = unitField.enum || [];
  return {
    enum: enums,
    enumNames: (unitField.enumNames || enums).slice(0, enums.length),
  };
}

export function shouldShowUnit(schema, formDataOrCategory, unit) {
  return getUnitOptions(schema, formDataOrCategory, unit).enum.length > 0;
}

/**
 * Spec / conditional fields for the current form selection.
 * Accepts either full formData or legacy (category, unit) args.
 */
export function getSpecFields(schema, formDataOrCategory, unit) {
  const formData = normalizeFormDataArg(schema, formDataOrCategory, unit);
  const categoryKey = getCategoryFieldKey(schema);
  const resolved = getResolvedFields(schema, formData);
  const specs = {};

  Object.entries(resolved).forEach(([key, fieldSchema]) => {
    if (SPECIAL_FIELD_KEYS.has(key)) return;
    if (key === categoryKey) return;
    specs[key] = fieldSchema;
  });

  return specs;
}

export function orderSpecEntries(specFields, uiOrder = []) {
  const entries = Object.entries(specFields || {});
  if (!Array.isArray(uiOrder) || !uiOrder.length) return entries;

  const rank = new Map();
  uiOrder.forEach((key, index) => {
    if (key !== "*") rank.set(key, index);
  });

  return entries.sort(([a], [b]) => {
    const ai = rank.has(a) ? rank.get(a) : Number.MAX_SAFE_INTEGER;
    const bi = rank.has(b) ? rank.get(b) : Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    return a.localeCompare(b);
  });
}

/** Prefer schema title exactly as returned by the API (no case rewriting). */
export function prettyFieldLabel(key, fieldSchema) {
  if (typeof fieldSchema?.title === "string" && fieldSchema.title.length) {
    return fieldSchema.title;
  }
  return String(key || "").replace(/_/g, " ");
}

export function getSchemaFieldTitle(schema, key, fallback) {
  const aliases = {
    "Unit Price": ["Unit Price", "unit_price"],
    unit_price: ["unit_price", "Unit Price"],
    min_value: ["min_value", "Min Value", "Min Price"],
    max_value: ["max_value", "Max Value", "Max Price"],
  };
  const keysToTry = aliases[key] || [key];
  const resolved = getResolvedFields(schema, {});

  for (const candidate of keysToTry) {
    const fromProperties = schema?.properties?.[candidate];
    if (typeof fromProperties?.title === "string" && fromProperties.title.length) {
      return fromProperties.title;
    }
    if (typeof resolved?.[candidate]?.title === "string" && resolved[candidate].title.length) {
      return resolved[candidate].title;
    }
  }

  if (typeof fallback === "string" && fallback.length) return fallback;
  return prettyFieldLabel(key, null);
}

export function getFieldControl(fieldSchema) {
  if (fieldSchema?.enum?.length) {
    return {
      kind: "select",
      enum: fieldSchema.enum,
      enumNames: fieldSchema.enumNames || fieldSchema.enum,
    };
  }
  if (fieldSchema?.type === "boolean") return { kind: "boolean" };
  if (fieldSchema?.type === "number" || fieldSchema?.type === "integer") {
    return { kind: "number" };
  }
  return { kind: "text" };
}

function defaultForField(fieldSchema, key) {
  if (fieldSchema?.default !== undefined) return fieldSchema.default;
  if (key === "min_value" || key === "max_value") return 0;
  if (fieldSchema?.enum?.length) return fieldSchema.enum[0];
  if (fieldSchema?.type === "boolean") return false;
  if (fieldSchema?.type === "number" || fieldSchema?.type === "integer") return 0;
  return "";
}

function findEnumMatch(enums, candidate) {
  if (!candidate || !enums?.length) return null;
  return (
    enums.find(
      (value) => String(value).toLowerCase() === String(candidate).toLowerCase()
    ) || null
  );
}

function inferCategory(raw, schema) {
  const categoryKey = getCategoryFieldKey(schema);
  const enums = schema?.properties?.[categoryKey]?.enum || [];
  const candidates = [
    raw?.category,
    raw?.Category,
    raw?.resource_category,
    raw?.resource_type,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const match = findEnumMatch(enums, candidate);
    if (match) return match;
    const lower = String(candidate).toLowerCase();
    if (lower === "service") return findEnumMatch(enums, "Service") || "Service";
    if (lower === "storage") return findEnumMatch(enums, "Storage") || "Storage";
    if (lower === "computational" || lower === "compute") {
      return findEnumMatch(enums, "Computational") || "Computational";
    }
  }

  if (raw?.compute_type || raw?.computational_type || raw?.gpu_type || raw?.cpu_type) {
    return findEnumMatch(enums, "Computational") || enums[0];
  }
  if (raw?.storage_type) return findEnumMatch(enums, "Storage") || enums[0];
  if (raw?.service || raw?.service_type) return findEnumMatch(enums, "Service") || enums[0];
  return enums[0];
}

function inferUnit(raw) {
  return (
    raw?.Unit ||
    raw?.unit ||
    raw?.specification?.unit ||
    ""
  );
}

function syncPricingDefaults(defaults, resolved) {
  if (resolved.unit_price && defaults["Unit Price"] === undefined) {
    defaults["Unit Price"] = defaultForField(resolved.unit_price, "unit_price");
  }
  if (resolved["Unit Price"] && defaults["Unit Price"] === undefined) {
    defaults["Unit Price"] = defaultForField(resolved["Unit Price"], "Unit Price");
  }
  if (resolved.min_value && defaults.min_value === undefined) {
    defaults.min_value = defaultForField(resolved.min_value, "min_value");
  }
  if (resolved.max_value && defaults.max_value === undefined) {
    defaults.max_value = defaultForField(resolved.max_value, "max_value");
  }
}

function fillResolvedDefaults(defaults, schema) {
  const resolved = getResolvedFields(schema, defaults);

  Object.entries(resolved).forEach(([key, fieldSchema]) => {
    if (defaults[key] === undefined || defaults[key] === "") {
      defaults[key] = defaultForField(fieldSchema, key);
    }
  });

  const unitOptions = getUnitOptions(schema, defaults);
  if (unitOptions.enum.length) {
    if (!unitOptions.enum.includes(defaults.Unit)) {
      defaults.Unit = unitOptions.enum[0];
    }
  } else {
    defaults.Unit = "";
  }

  syncPricingDefaults(defaults, resolved);

  // Second pass after discriminators (e.g. computational_type) are filled.
  const resolvedAgain = getResolvedFields(schema, defaults);
  Object.entries(getSpecFields(schema, defaults)).forEach(([key, fieldSchema]) => {
    if (defaults[key] === undefined || defaults[key] === "") {
      defaults[key] = defaultForField(fieldSchema, key);
    }
  });
  syncPricingDefaults(defaults, resolvedAgain);

  return defaults;
}

function applyEditPrefill(defaults, editData, schema) {
  const raw = editData?.raw || editData || {};
  const spec = {
    ...(raw.specification || {}),
    ...(raw.service || {}),
  };
  const categoryKey = getCategoryFieldKey(schema);

  defaults["Resource Name"] = raw.name || editData.name || defaults["Resource Name"] || "";
  defaults["Resource Description"] =
    raw.description ||
    raw.resource_description ||
    editData.description ||
    defaults["Resource Description"] ||
    "";

  const category = inferCategory(raw, schema);
  defaults[categoryKey] = category || defaults[categoryKey];

  if (raw.computational_type || raw.compute_type) {
    defaults.computational_type = raw.computational_type || raw.compute_type;
  }

  const unitOptions = getUnitOptions(schema, defaults);
  const inferredUnit = inferUnit(raw);
  defaults.Unit = unitOptions.enum.includes(inferredUnit)
    ? inferredUnit
    : unitOptions.enum[0] || "";

  defaults["Unit Price"] =
    raw.unit_price ??
    raw.price ??
    editData.unit_price ??
    editData.pricePerUnit ??
    defaults["Unit Price"] ??
    0;

  defaults.min_value = raw.metadata?.min ?? raw.min_price ?? editData.min ?? defaults.min_value ?? 0;
  defaults.max_value = raw.metadata?.max ?? raw.max_price ?? editData.max ?? defaults.max_value ?? 0;
  defaults.Avalability = raw.availability ?? raw.Avalability ?? defaults.Avalability ?? true;

  fillResolvedDefaults(defaults, schema);

  const specFields = getSpecFields(schema, defaults);
  Object.keys(specFields).forEach((key) => {
    const value = spec[key] ?? raw[key] ?? editData[key];
    if (value !== undefined && value !== null && value !== "") {
      defaults[key] = value;
    }
  });

  if (spec.region && (defaults.location === "" || defaults.location === undefined)) {
    defaults.location = spec.region;
  }
}

export function buildResourceFormDefaults(schema, editData) {
  const defaults = {};

  Object.entries(schema?.properties || {}).forEach(([key, fieldSchema]) => {
    if (key === "Unit") {
      defaults[key] = "";
      return;
    }
    defaults[key] = defaultForField(fieldSchema, key);
  });

  fillResolvedDefaults(defaults, schema);

  if (editData) applyEditPrefill(defaults, editData, schema);
  return defaults;
}

export function applyCategoryChange(prev, schema, category) {
  const categoryKey = getCategoryFieldKey(schema);
  const next = { ...prev, [categoryKey]: category };

  // Drop previous category-specific values so nested branches can re-default.
  Object.keys(getSpecFields(schema, prev)).forEach((key) => {
    delete next[key];
  });
  delete next.computational_type;
  delete next.Unit;

  return fillResolvedDefaults(next, schema);
}

export function applyUnitChange(prev, schema, unit) {
  const next = { ...prev, Unit: unit };
  return fillResolvedDefaults(next, schema);
}

/** When a nested discriminator (e.g. computational_type) changes, refresh dependent fields. */
export function applySpecFieldChange(prev, schema, key, value) {
  const next = { ...prev, [key]: value };
  const before = getSpecFields(schema, prev);
  const after = getSpecFields(schema, next);

  Object.keys(before).forEach((fieldKey) => {
    if (!(fieldKey in after) && fieldKey !== key) {
      delete next[fieldKey];
    }
  });

  return fillResolvedDefaults(next, schema);
}

export function isNestedDiscriminatorField(schema, formData, key) {
  if (!key || !schema) return false;
  const resolved = getResolvedFields(schema, formData);
  const field = resolved[key];
  if (!field?.enum?.length) return false;

  const walk = (node) => {
    if (!node?.dependencies) return false;
    if (node.dependencies[key]?.oneOf) return true;
    return Object.values(node.dependencies).some((dependency) => {
      if (!Array.isArray(dependency?.oneOf)) return false;
      return dependency.oneOf.some((branch) => walk(branch));
    });
  };

  return walk({
    properties: schema.properties,
    dependencies: schema.dependencies,
  });
}

export function coerceResourceFieldValue(key, value) {
  if (!RESOURCE_NUMERIC_KEYS.includes(key)) return value;
  if (value === "") return "";
  const numeric = Number(value);
  if (Number.isNaN(numeric) || numeric < 0) return 0;
  return numeric;
}

export function buildResourcePayload(formData, schema) {
  const categoryKey = getCategoryFieldKey(schema);
  const category = formData[categoryKey];
  const showUnit = shouldShowUnit(schema, formData);
  const specFields = getSpecFields(schema, formData);
  const unitPrice =
    formData["Unit Price"] === "" || formData["Unit Price"] === undefined
      ? formData.unit_price === "" || formData.unit_price === undefined
        ? 0
        : Number(formData.unit_price || 0)
      : Number(formData["Unit Price"] || 0);
  const min = formData.min_value === "" ? 0 : Number(formData.min_value || 0);
  const max = formData.max_value === "" ? 0 : Number(formData.max_value || 0);

  const payload = {
    "Resource Name": formData["Resource Name"],
    "Resource Description": formData["Resource Description"],
    [categoryKey]: category,
    "Unit Price": unitPrice,
    unit_price: unitPrice,
    min_value: min,
    max_value: max,
    Avalability: formData.Avalability !== false,
    metadata: {
      ui_type: max === 0 ? "input" : "slider",
      min,
      max,
    },
  };

  if (showUnit) payload.Unit = formData.Unit;

  Object.keys(specFields).forEach((key) => {
    payload[key] = formData[key];
  });

  return payload;
}
